/**
 * DataBest — Dashboard data hook.
 * Fetches KPIs + radar + trend + breakdown in parallel whenever filters change,
 * with a small debounce so rapid filter toggles don't spam the API.
 */
import { useEffect, useRef, useState } from "react";
import { dashboardApi } from "@/lib/dashboardApi";

const useDashboardData = (filters) => {
    const [data, setData] = useState({
        kpis: null, radar: null, trend: null, breakdown: null,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const timerRef = useRef(null);
    const seqRef = useRef(0);

    useEffect(() => {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(async () => {
            const mySeq = ++seqRef.current;
            setLoading(true);
            setError(null);
            try {
                const [kpis, radar, trend, breakdown] = await Promise.all([
                    dashboardApi.kpis(filters),
                    dashboardApi.radar(filters),
                    dashboardApi.trend({ ...filters, granularity: filters.granularity || "day" }),
                    dashboardApi.breakdown({ ...filters, group_by: filters.group_by || "individual" }),
                ]);
                // Only apply if this is the freshest request.
                if (mySeq === seqRef.current) {
                    setData({ kpis, radar, trend, breakdown });
                }
            } catch (err) {
                if (mySeq === seqRef.current) {
                    setError(err);
                }
            } finally {
                if (mySeq === seqRef.current) {
                    setLoading(false);
                }
            }
        }, 250);

        return () => clearTimeout(timerRef.current);
    }, [
        filters.from, filters.to, filters.individual_id, filters.life_area,
        filters.granularity, filters.group_by,
    ]);

    return { ...data, loading, error };
};

export default useDashboardData;
